import { Contact, ContactInput, ContactsResponse, ContactStats, Category, CategoryInput, CategoriesResponse, ImportResult, TenantCustomField, TenantCustomFieldInput } from '../types';

const API_BASE_URL = '/api';

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');
    const tenantId = localStorage.getItem('selected_tenant_id');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
        const quotaError = new Error(error.message || error.error || 'Limite atingido');
        (quotaError as any).isQuotaError = true;
        (quotaError as any).upgradeRequired = true;
        throw quotaError;
      }

      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.sortField) searchParams.set('sortField', params.sortField);
    if (params?.sortDirection) searchParams.set('sortDirection', params.sortDirection);

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContactStats(): Promise<ContactStats> {
    return this.request<ContactStats>('/contatos/stats');
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>('/contatos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  // Campos personalizados (custom fields)
  async getCustomFields(): Promise<TenantCustomField[]> {
    return this.request<TenantCustomField[]>('/contatos/campos-personalizados');
  }

  async createCustomField(data: TenantCustomFieldInput): Promise<TenantCustomField> {
    return this.request<TenantCustomField>('/contatos/campos-personalizados', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomField(id: string, data: Partial<TenantCustomFieldInput>): Promise<TenantCustomField> {
    return this.request<TenantCustomField>(`/contatos/campos-personalizados/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomField(id: string): Promise<void> {
    await this.request<void>(`/contatos/campos-personalizados/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderCustomFields(orderedIds: string[]): Promise<void> {
    await this.request<void>('/contatos/campos-personalizados/reorder', {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getAllCategories(): Promise<Category[]> {
    // Usar rota paginada com pageSize alto para garantir todas as categorias
    // (evita limite de 10 quando /categorias/all falha ou não existe)
    try {
      const response = await this.getCategories({ page: 1, pageSize: 10000 });
      return response.categories ?? [];
    } catch (error) {
      console.error('Erro em getAllCategories, tentando /categorias/all:', error);
      try {
        return await this.request<Category[]>('/categorias/all');
      } catch (fallbackError) {
        console.error('Erro no fallback de getAllCategories:', fallbackError);
        throw fallbackError;
      }
    }
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>('/categorias', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  // CSV Import methods
  async importCSV(
    file: File,
    categoriaId?: string,
    options?: { duplicateAction: 'skip' | 'update_category'; duplicateCategoriaId?: string }
  ): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);
    if (categoriaId) {
      formData.append('categoriaId', categoriaId);
    }
    formData.append('duplicateAction', options?.duplicateAction ?? 'skip');
    if (options?.duplicateCategoriaId) {
      formData.append('duplicateCategoriaId', options.duplicateCategoriaId);
    }

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  async downloadXLSXTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template-xlsx`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  async previewCSV(formData: FormData): Promise<{ columns: string[]; preview: any[] }> {
    const token = localStorage.getItem('auth_token');
    const tenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/preview`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async importCSVWithMapping(formData: FormData): Promise<ImportResult> {
    const token = localStorage.getItem('auth_token');
    const tenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/import-with-mapping`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Bulk operations
  async post(endpoint: string, data: any): Promise<any> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Chatwoot integration
  async getChatwootTags(): Promise<{ tags: Array<{ name: string; count: number }> }> {
    return this.request('/chatwoot/tags');
  }

  async syncChatwootContacts(tagMappings: Array<{ chatwootTag: string; categoryId: string }>): Promise<{ imported: number; updated: number }> {
    return this.request('/chatwoot/sync', {
      method: 'POST',
      body: JSON.stringify({ tagMappings }),
    });
  }

  // Perfex CRM Integration
  async getPerfexLeads(): Promise<{ success: boolean; leads: any[]; total: number }> {
    return this.request('/perfex/leads');
  }

  async importPerfexLeads(leadIds: string[], categoryId: string): Promise<{ success: boolean; imported: number; updated: number; errors: number }> {
    return this.request('/perfex/import', {
      method: 'POST',
      body: JSON.stringify({ leadIds, categoryId }),
    });
  }

  async getPerfexStatuses(): Promise<{ success: boolean; statuses: Array<{ id: string; name: string; color?: string }> }> {
    return this.request('/perfex/statuses');
  }

  async getPerfexSources(): Promise<{ success: boolean; sources: Array<{ id: string; name: string }> }> {
    return this.request('/perfex/sources');
  }

  async getPerfexStaff(): Promise<{ success: boolean; staff: Array<{ staffid: string; firstname: string; lastname: string; email: string }> }> {
    return this.request('/perfex/staff');
  }
}

export const apiService = new ApiService();
export const api = apiService;